<script lang="ts">
  /**
   * Every question behind one recurring topic, as the same excerpt list a weekly theme
   * expands into.
   *
   * ONE INTERACTION IN BOTH TABS. `This week` expands a theme into `<AskCard>`s;
   * `Recurring` did something else entirely — it opened a drawer with three tabs — so
   * the same act (open a row, read what was asked) had two different answers depending
   * on which tab you were in. This is the weekly expansion, over a topic's whole life
   * instead of one week.
   *
   * POSTS AND COMMENTS TOGETHER, newest first. The drawer separated them because it had
   * tabs to fill; a reader asking "what do they keep asking about this" does not care
   * which surface it was typed into, and half of all questions are comments, so
   * splitting them hid half the answer behind a tab.
   */
  import AskCard from './AskCard.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import { fetchTopicHistory, type DigestQuote, type TopicHistory } from '@api';

  let {
    skoolId,
    topicId,
    onopenthread,
  }: {
    skoolId: number;
    topicId: number;
    onopenthread: (q: DigestQuote) => void;
  } = $props();

  let data = $state<TopicHistory | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  /** A topic running eleven weeks holds thirty-odd questions; a page at a time keeps
   *  the row a row rather than a wall the next topic sits below. */
  const PAGE = 8;
  let shown = $state(PAGE);

  $effect(() => {
    const id = topicId;
    loading = true;
    error = null;
    fetchTopicHistory(skoolId, id)
      .then((d) => (data = d))
      .catch((e: unknown) => (error = e instanceof Error ? e.message : String(e)))
      .finally(() => (loading = false));
  });

  /** Newest first — what they are asking NOW is the useful end of a recurring topic. */
  const asked = $derived.by(() => {
    const all = [...(data?.posts ?? []), ...(data?.comments ?? [])];
    return all.sort((a, b) => (b.asked_at ?? '').localeCompare(a.asked_at ?? ''));
  });

  /** The most recent week's verdict — coverage is judged per week, and where a topic
   *  stands NOW is the thing worth repeating above the evidence. */
  const latest = $derived(data?.weeks?.length ? data.weeks[data.weeks.length - 1] : null);

  const ACTION_WORD: Record<string, string> = {
    create_lesson: 'Make a lesson',
    update_lesson: 'Update a lesson',
    make_content: 'Post about it',
    none: '',
  };
</script>

<div class="border-t hair bg-base-200/30 px-4 py-3">
  {#if loading}
    <StateBlock state="loading" compact />
  {:else if error}
    <StateBlock state="error" message={error} compact />
  {:else}
    {#if latest?.action_text}
      <div class="rounded-box mb-3 border hair bg-base-100 px-3 py-2.5">
        {#if ACTION_WORD[latest.action_kind ?? 'none']}
          <!-- Not `.eyebrow`: it is unlayered in app.css and its colour beats a
               Tailwind utility, so `text-primary` on it silently does nothing. -->
          <div class="mb-1 text-[0.58rem] text-primary font-mono tracking-[0.13em] uppercase">
            {ACTION_WORD[latest.action_kind ?? 'none']}
          </div>
        {/if}
        <p class="redact text-[0.83rem] leading-relaxed">{latest.action_text}</p>
      </div>
    {/if}

    <div class="space-y-2">
      {#each asked.slice(0, shown) as q (q.question_id)}
        <AskCard quote={q} onopen={() => onopenthread(q)} />
      {:else}
        <StateBlock state="empty" message="Nothing recorded for this topic yet." compact />
      {/each}
    </div>

    {#if asked.length > shown}
      <button
        class="eyebrow mt-2 w-full rounded-btn border hair py-2 text-[0.62rem] text-base-content/60 hover:bg-base-200"
        onclick={() => (shown += PAGE)}
      >
        Show {Math.min(PAGE, asked.length - shown)} more · {asked.length - shown} left
      </button>
    {/if}
  {/if}
</div>
